import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import supabase from '../supabase';
import SafeIcon from '../common/SafeIcon';
import { FiMail, FiCalendar, FiChevronRight, FiLoader } from 'react-icons/fi';
import { motion } from 'framer-motion';

const NewsletterArchive = () => {
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNewsletters = async () => {
      try {
        const { data, error } = await supabase
          .from('newsletters_20240320')
          .select('id, subject, slug, created_at')
          .order('created_at', { ascending: false });

        if (error) throw error;
        setNewsletters(data || []);
      } catch (err) {
        console.error('Error fetching newsletters:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchNewsletters();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] bg-stone-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-2xl animate-spin mx-auto mb-6"></div>
          <p className="text-stone-500 font-black uppercase tracking-widest text-xs animate-pulse">Loading Archive...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-32 pb-20 px-4">
      <div className="max-w-4xl mx-auto">
        <header className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-3xl mb-6 text-emerald-600">
            <SafeIcon icon={FiMail} className="text-3xl" />
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-stone-900 uppercase tracking-tighter mb-4">Newsletter Archive</h1>
          <p className="text-stone-500 font-bold uppercase tracking-widest text-xs md:text-sm max-w-xl mx-auto leading-relaxed">
            Catch up on past editions of The Wild Report, featuring our top job picks, industry news, and adventure inspiration.
          </p>
        </header>

        <div className="space-y-4">
          {newsletters.length > 0 ? (
            newsletters.map((issue, index) => (
              <motion.div
                key={issue.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/newsletters/${issue.slug}`}
                  className="group block bg-white border border-stone-200 rounded-[24px] p-6 md:p-8 hover:border-emerald-500 hover:shadow-xl hover:shadow-emerald-900/5 transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 text-stone-400 mb-2">
                        <SafeIcon icon={FiCalendar} className="text-sm" />
                        <span className="text-[10px] font-black uppercase tracking-widest">
                          {new Date(issue.created_at).toLocaleDateString(undefined, { 
                            year: 'numeric', 
                            month: 'long' 
                          })}
                        </span>
                      </div>
                      <h3 className="text-xl md:text-2xl font-black text-stone-900 uppercase tracking-tight group-hover:text-emerald-600 transition-colors">
                        {issue.subject}
                      </h3>
                    </div>
                    <div className="w-12 h-12 rounded-2xl bg-stone-50 flex items-center justify-center text-stone-300 group-hover:bg-emerald-500 group-hover:text-white transition-all">
                      <SafeIcon icon={FiChevronRight} className="text-xl" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))
          ) : (
            <div className="text-center py-20 bg-white rounded-[40px] border border-stone-200 border-dashed">
              <p className="text-stone-400 font-black uppercase tracking-widest text-sm">No issues archived yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsletterArchive;